import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckSquare,
  Copy,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Save,
  Square,
} from 'lucide-react';
import {
  desktopRibbonButtonClass,
  desktopRibbonIconClass,
  desktopRibbonLabelClass,
} from '../../../components/ribbon/DesktopRibbonControls';

interface LstRibbonArquivoSlotProps {
  onSalvarPadrao: () => void;
  onSalvarComo: () => void;
  onExportPdf: () => void;
  onExportExcel: () => void;
  compact: boolean;
  onToggleCompact: () => void;
  hideDescriptions: boolean;
  onToggleHideDescriptions: () => void;
}

type MenuItem = {
  id: string;
  label: string;
  icon?: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  onClick?: () => void;
  divider?: boolean;
  checked?: boolean;
};

/** Menu Arquivo — primeira posição do ribbon (padrão desktop enterprise). */
export const LstRibbonArquivoSlot: React.FC<LstRibbonArquivoSlotProps> = (props) => {
  const {
    onSalvarPadrao,
    onSalvarComo,
    onExportPdf,
    onExportExcel,
    compact,
    onToggleCompact,
    hideDescriptions,
    onToggleHideDescriptions,
  } = props;

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  const items: MenuItem[] = [
    { id: 'salvar', label: 'Salvar Padrão', icon: Save, onClick: onSalvarPadrao },
    { id: 'salvar-como', label: 'Salvar Como (Duplicar Matriz)', icon: Copy, onClick: onSalvarComo },
    { id: 'div-1', label: '', divider: true },
    { id: 'exp-pdf', label: 'Exportar PDF', icon: FileText, onClick: onExportPdf },
    { id: 'exp-xls', label: 'Exportar Excel', icon: FileSpreadsheet, onClick: onExportExcel },
    { id: 'div-2', label: '', divider: true },
    { id: 'compacto', label: 'Modo Compacto', icon: compact ? CheckSquare : Square, onClick: onToggleCompact, checked: compact },
    { id: 'ocultar-desc', label: 'Ocultar Descrições', icon: hideDescriptions ? CheckSquare : Square, onClick: onToggleHideDescriptions, checked: hideDescriptions },
  ];

  const toggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left });
    setOpen((v) => !v);
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        title="Arquivo — salvar, exportar e exibição"
        className={desktopRibbonButtonClass}
      >
        <span className={desktopRibbonIconClass}>
          <FolderOpen size={18} strokeWidth={1.5} />
        </span>
        <span className="flex items-center gap-[2px]">
          <span className={desktopRibbonLabelClass}>Arquivo</span>
          <svg width="7" height="5" viewBox="0 0 7 5" fill="none" aria-hidden="true" className="opacity-60">
            <path d="M1 1L3.5 4L6 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {open &&
        createPortal(
          <div
            className="fixed z-[9999] min-w-[200px] rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
            style={{ top: pos.top, left: pos.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {items.map((item) =>
              item.divider ? (
                <div key={item.id} className="my-1 border-t border-slate-100 dark:border-slate-800" />
              ) : (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    item.onClick?.();
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11px] text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  {item.icon && <item.icon size={13} strokeWidth={1.5} className="shrink-0 text-slate-400" />}
                  <span className="flex-1">{item.label}</span>
                  {item.checked && <CheckSquare size={12} className="text-blue-600" />}
                </button>
              ),
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

export default LstRibbonArquivoSlot;
